/**
 * Resolve file URL by prepending API base URL if needed
 * Handles both relative paths (/uploads/file.jpg) and absolute URLs
 */
import { getApiBaseUrlAsync } from '../config/apiConfig';

let cachedBaseUrl = null;

const stripApiSuffix = (apiBaseUrl) => (apiBaseUrl || '').replace(/\/api\/?$/, '');

const getBaseUrl = async () => {
  if (cachedBaseUrl) {
    return cachedBaseUrl;
  }

  try {
    const apiBaseUrl = await getApiBaseUrlAsync();
    cachedBaseUrl = stripApiSuffix(apiBaseUrl);
    return cachedBaseUrl;
  } catch (error) {
    console.warn('Failed to get API base URL:', error);
    return '';
  }
};

/** Prime sync cache — call after API URL init for correct img src on first render */
export const primeFileUrlCache = async () => {
  try {
    const apiBaseUrl = await getApiBaseUrlAsync();
    cachedBaseUrl = stripApiSuffix(apiBaseUrl);
  } catch {
    // ignore — resolveFileUrlSync falls back to relative path
  }
};

/**
 * Resolve a file URL by prepending the API base URL if it's a relative path
 * @param {string} fileUrl - The file URL (can be relative or absolute)
 * @returns {Promise<string>} - The resolved URL
 */
export const resolveFileUrl = async (fileUrl) => {
  if (!fileUrl) {
    return null;
  }

  // If already an absolute URL (http/https), return as is
  if (fileUrl.startsWith('http://') || fileUrl.startsWith('https://')) {
    return fileUrl;
  }

  // If it's a data URL (base64), return as is
  if (fileUrl.startsWith('data:')) {
    return fileUrl;
  }

  // For relative paths, prepend base URL
  try {
    const baseUrl = await getBaseUrl();
    if (!baseUrl) {
      return fileUrl; // Fallback to original if base URL unavailable
    }

    // Ensure proper path joining
    const normalizedPath = fileUrl.startsWith('/') ? fileUrl : `/${fileUrl}`;
    return `${baseUrl}${normalizedPath}`;
  } catch (error) {
    console.warn('Failed to resolve file URL:', error);
    return fileUrl; // Fallback to original
  }
};

/**
 * Synchronous version that uses cached base URL
 * Use this in render functions where async is not possible
 * @param {string} fileUrl - The file URL
 * @returns {string} - The resolved URL (may be incomplete if base URL not cached)
 */
export const resolveFileUrlSync = (fileUrl) => {
  if (!fileUrl) {
    return null;
  }

  // If already an absolute URL, return as is
  if (fileUrl.startsWith('http://') || fileUrl.startsWith('https://')) {
    return fileUrl;
  }

  // If it's a data URL, return as is
  if (fileUrl.startsWith('data:')) {
    return fileUrl;
  }

  // Use cached base URL if available
  if (cachedBaseUrl) {
    const normalizedPath = fileUrl.startsWith('/') ? fileUrl : `/${fileUrl}`;
    return `${cachedBaseUrl}${normalizedPath}`;
  }

  // Fallback: return original (will be resolved on next render)
  return fileUrl;
};
